import { Button } from '@mui/material';
import PropTypes from 'prop-types';
import { useResolutionScale } from '../../hooks';

/**
 * Custom button for message overlays.
 *
 * Props:
 * - variant: 'contained' | 'outlined'
 * - label: string (button text)
 * - onClick: function
 * - themeMode: 'dark' | 'light'
 * - buttonGradient: string (for contained style)
 * - palette: theme palette object
 */
const MessageButton = ({
	variant = 'contained',
	label,
	onClick,
	themeMode,
	palette,
	buttonGradient,
	scaleOverride,
	...rest
}) => {
	const { scaleValue } = useResolutionScale();
	const scale = scaleOverride || scaleValue;

	const commonStyles = {
		fontFamily: 'JLR Emeric, sans-serif',
		fontWeight: 600,
		fontSize: `${scale(20)}px`,
		borderRadius: `${scale(16)}px`,
		textTransform: 'none',
		px: `${scale(28)}px`,
		py: `${scale(14)}px`,
		minWidth: `${scale(150)}px`,
	};

	const outlinedStyles = {
		...commonStyles,
		color:
			themeMode === 'dark'
				? palette.dark.textPrimary
				: palette.light.textPrimary,
		borderColor:
			themeMode === 'dark'
				? palette.dark.border
				: palette.light.border,
		borderWidth: '2px',
		'&:hover': {
			borderColor:
				themeMode === 'dark'
					? 'rgba(255,255,255,0.5)'
					: 'rgba(45,74,45,0.5)',
		},
	};

	const containedStyles = {
		...commonStyles,
		background: buttonGradient,
		color: '#fff',
		'&:hover': {
			background: buttonGradient,
		},
	};

	return (
		<Button
			variant={variant}
			onClick={onClick}
			sx={variant === 'outlined' ? outlinedStyles : containedStyles}
			{...rest}
		>
			{label}
		</Button>
	);
};

MessageButton.propTypes = {
	variant: PropTypes.oneOf(['contained', 'outlined']),
	label: PropTypes.string.isRequired,
	onClick: PropTypes.func.isRequired,
	themeMode: PropTypes.string.isRequired,
	palette: PropTypes.object.isRequired,
	buttonGradient: PropTypes.string,
	scaleOverride: PropTypes.func,
};

export default MessageButton;